import ApiWithLogging from '../utils/apiWithLogging';
import { rawAxios, refreshTokenSingleFlight } from '../utils/apiWithLogging';
import { UserPermissions } from '../types/permission';

interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthResponse {
  token?: string;
  user?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  // สำหรับ FastAPI OAuth2 response
  access_token?: string;
  token_type?: string;
  user_id?: number;
  username?: string;
  email?: string;
  full_name?: string;
  role_id?: number | null;
  role_name?: string | null;
  permissions?: UserPermissions | null;
  is_superuser?: boolean;
  is_superadmin?: boolean;
  // Refresh token fields
  refresh_token?: string;
  expires_in?: number;
}

// บริการยืนยันตัวตน
const authService = {
  // เข้าสู่ระบบ
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // สร้าง FormData สำหรับการร้องขอ OAuth2
      const formData = new URLSearchParams();
      // BUG-S2579-001 (KD3 NEG-AUTH-020): trim ชื่อผู้ใช้ก่อนส่ง — ผู้ใช้วางมาจาก Excel/แชทมักติดช่องว่าง
      // หน้า/หลัง แล้วได้ 401 ทั้งที่รหัสถูก · รหัสผ่านไม่ trim โดยเจตนา (ช่องว่างเป็นส่วนหนึ่งของรหัสได้)
      formData.set('username', credentials.username.trim());
      formData.set('password', credentials.password);

      const response = await ApiWithLogging.post<AuthResponse>('/auth/login', formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        },
        {
          service: 'authService',
          action: 'user_login',
          entityType: 'authentication',
          details: { username: credentials.username }
        }
      );

      // บันทึกข้อมูลการเข้าสู่ระบบลงใน localStorage
      if (response.access_token) {
        // ถ้า API ส่ง access_token กลับมา
        localStorage.setItem('access_token', response.access_token);

        // เก็บ refresh_token + expires_at
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }
        if (response.expires_in) {
          const expiresAt = Date.now() + (response.expires_in * 1000);
          localStorage.setItem('token_expires_at', expiresAt.toString());
        }

        // ตรวจสอบว่าเป็น Super Admin หรือไม่
        let userRole = 'user';
        if (response.is_superadmin) {
          userRole = 'superadmin';
        } else if (response.role_id === undefined || response.role_id === 1 || response.is_superuser) {
          userRole = 'admin';
        }

        // Keep original user ID from backend
        const userId = response.user_id || 0;

        // Get permissions from response
        const permissions = response.permissions || {};

        const user = {
          id: userId,
          username: response.username || '',
          name: response.full_name || 'ผู้ใช้งาน',
          email: response.email || '',
          role: userRole,
          role_id: response.role_id,
          role_name: response.role_name || null,
          permissions: permissions,
          // BUG-SA37 FIX: backend sends `is_superuser` (never `is_superadmin`), so the
          // super-admin bypass in AuthContext.hasPermission / usePermission (which read
          // user.is_superadmin) was dead for is_superuser accounts — they were gated like a
          // plain role. Fall back to is_superuser (same full-authority flag the backend
          // already bypasses every permission on) so FE ↔ BE agree.
          is_superadmin: response.is_superadmin || response.is_superuser || false,
          is_superuser: response.is_superuser || false
        };

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('permissions', JSON.stringify(permissions));
      } else if (response.token) {
        // กรณี API ส่ง token กลับมา (ตามโครงสร้างเดิม)
        localStorage.setItem('access_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
      } else {
        console.error('Login error: No token received in response');
        throw new Error('ไม่พบข้อมูลการยืนยันตัวตนในการตอบกลับ');
      }

      return response as AuthResponse;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // ออกจากระบบ
  logout(): void {
    // Fire-and-forget: เรียก server logout endpoint
    const refreshToken = localStorage.getItem('refresh_token');
    const accessToken = localStorage.getItem('access_token');
    if (refreshToken) {
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      rawAxios.post('/auth/logout', { refresh_token: refreshToken }, { headers }).catch(() => {
        // Ignore error — เราต้องการ logout อยู่แล้ว
      });
    }

    // ลบข้อมูลการเข้าสู่ระบบออกจาก localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');

    // ลบข้อมูลเก่าจาก sessionStorage ด้วย (เผื่อมีเหลืออยู่)
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('permissions');
  },

  // Refresh access token ด้วย refresh token
  // DELEGATE ไปที่ refreshTokenSingleFlight (single source of truth ใน apiWithLogging)
  // เพื่อไม่ให้มี refresh path ที่ยิง /auth/refresh แข่งกันเอง (family revocation)
  async refreshAccessToken(): Promise<boolean> {
    const outcome = await refreshTokenSingleFlight();
    return outcome.ok;
  },

  // ตรวจสอบว่า token จะหมดอายุเร็วๆ นี้ (ภายใน 2 นาที)
  isTokenExpiringSoon(): boolean {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return false;

    const timeRemaining = parseInt(expiresAt) - Date.now();
    return timeRemaining > 0 && timeRemaining < 120000; // < 2 minutes
  },

  // ดึง permissions จาก localStorage
  getPermissions(): UserPermissions {
    const permissionsJson = localStorage.getItem('permissions');
    if (!permissionsJson) return {};
    try {
      return JSON.parse(permissionsJson);
    } catch {
      return {};
    }
  },

  // ตรวจสอบว่ามีการยืนยันตัวตนหรือไม่
  isAuthenticated(): boolean {
    // ตรวจสอบว่ามี token หรือไม่ - ลองดูจาก localStorage ก่อน
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!token) {
      // ถ้าไม่มี access token แต่มี refresh token → ยังนับว่า authenticated
      // (interceptor จะจัดการ refresh ให้เมื่อเจอ 401)
      const refreshToken = localStorage.getItem('refresh_token');
      return !!refreshToken;
    }

    // ตรวจสอบว่า token หมดอายุหรือไม่ (ถ้าเป็น JWT)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp && payload.exp < currentTime) {
        // Token หมดอายุแล้ว — แต่ถ้ามี refresh token ให้นับว่ายัง authenticated
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          console.log('🔄 Access token expired but refresh token available');
          return true;
        }
        this.logout();
        return false;
      }

      return true;
    } catch (error) {
      // ถ้า decode token ไม่ได้ — ถ้ามี refresh token ยังนับว่า authenticated
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        return true;
      }
      console.error('Invalid token format:', error);
      this.logout();
      return false;
    }
  },

  // ตรวจสอบ token กับ server
  async validateToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token) {
        return false;
      }

      // ส่ง request ไปตรวจสอบ token กับ server
      const userData = await ApiWithLogging.get('/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // BUG-S2004-001 (KD3 2026-09-02): sync permissions/role จาก server ทุกครั้งที่ validate
      // → ผู้ดูแลแก้สิทธิ์ role หรือเปลี่ยน role ของผู้ใช้ แล้วเมนู/route guard เปลี่ยนตามหลัง reload
      // โดยไม่ต้อง login ใหม่ (R10) — เดิม permissions มาจาก login response เท่านั้น
      if (userData && userData.permissions && typeof userData.permissions === 'object') {
        const nextPerms = userData.permissions;
        const nextJson = JSON.stringify(nextPerms);
        const prevJson = localStorage.getItem('permissions') || sessionStorage.getItem('permissions') || '{}';
        const storedUser = this.getCurrentUser();
        const roleChanged = !!storedUser && (
          storedUser.role_id !== userData.role_id ||
          (storedUser.role_name || null) !== (userData.role_name || null)
        );
        if (prevJson !== nextJson || roleChanged) {
          localStorage.setItem('permissions', nextJson);
          if (storedUser) {
            const updatedUser = {
              ...storedUser,
              permissions: nextPerms,
              role_id: userData.role_id,
              role_name: userData.role_name || null,
              is_superuser: !!userData.is_superuser,
              is_superadmin: !!(storedUser.is_superadmin || userData.is_superuser),
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
          window.dispatchEvent(new CustomEvent('auth:permissions-updated'));
        }
      }

      // ถ้าได้ข้อมูล user กลับมา แสดงว่า token valid
      return userData && (userData.id || userData.user_id);
    } catch (error: any) {
      console.error('Token validation failed:', error);

      // ถ้าเป็น 401 — ลอง refresh ผ่าน single-flight ก่อน
      if (error.response?.status === 401) {
        const r = await refreshTokenSingleFlight();
        if (r.ok === true) {
          console.log('🔄 Token refreshed during validation');
          return true;
        }
        if (r.reason === 'auth') {
          // refresh token หมดอายุจริง → logout
          this.logout();
          return false;
        }
        // transient (network/timeout/429/5xx) → อย่า logout, คง session ไว้ให้ retry ครั้งถัดไป
        console.warn('Transient error during refresh in validation, keeping session');
        return false;
      }

      if (error.response?.status === 403) {
        this.logout();
        return false;
      }

      // ถ้าเป็น network error หรือ server error - ไม่ logout แต่ return false
      console.warn('Network or server error during token validation, keeping user logged in');
      return false;
    }
  },

  // ดึงข้อมูลผู้ใช้ปัจจุบัน
  getCurrentUser(): any {
    const userJson = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  },

  // ขอรีเซ็ตรหัสผ่าน
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await ApiWithLogging.post<{ message: string }>('/auth/forgot-password', { email });
    return response;
  },

  // รีเซ็ตรหัสผ่านด้วย token
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await ApiWithLogging.post<{ message: string }>('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response;
  },
};

export default authService;
